import { FiAlertTriangle } from 'react-icons/fi';
import TextEdit from '../../../molecules/TextEdit';
import DateField from '../../../molecules/DateField';
import CurrencyInput from '../../../molecules/CurrencyInput';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import type { ErrorField } from '../../../../types/ErrorField';
import useLegalCaseDepositForm, { residualErrors } from '../../../../hooks/useLegalCaseDepositForm';
import type { AddJudicialDepositValue } from '../../../../hooks/useLegalCaseDeposits';

interface LegalCaseDepositFormProps {
    errorFields: ErrorField[];
    isSaving: boolean;
    onSubmit: (value: AddJudicialDepositValue) => Promise<boolean>;
    onCancel: () => void;
}

// Formulário de criação do depósito (o bloqueio). Só JSX + bindings: estado e regras vivem em
// useLegalCaseDepositForm. O levantamento é registrado depois, editando o depósito.
const LegalCaseDepositForm = ({
    errorFields,
    isSaving,
    onSubmit,
    onCancel,
}: LegalCaseDepositFormProps) => {
    const form = useLegalCaseDepositForm({ onSubmit });

    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CurrencyInput
                    label="Valor bloqueado"
                    prefix="R$"
                    fractionDigits={2}
                    value={form.blockedAmount}
                    onChange={form.setBlockedAmount}
                    errorMessage={getErrorMessage(errorFields, 'blockedAmount')}
                />
                <DateField
                    label="Data do bloqueio (opcional)"
                    value={form.blockedAt}
                    onChange={form.setBlockedAt}
                    errorMessage={getErrorMessage(errorFields, 'blockedAt')}
                />
            </div>

            <TextEdit
                label="Folhas dos autos (opcional)"
                value={form.courtRecordsPage}
                onChange={(e) => form.setCourtRecordsPage(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'courtRecordsPage')}
            />

            <TextEdit
                label="Observações (opcional)"
                value={form.description}
                onChange={(e) => form.setDescription(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'description')}
            />

            {/* Erros de validação (422) sem campo próprio na tela — não deixa sumir. */}
            {residualErrors(errorFields).map((e) => (
                <div key={e.fieldName} className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                    <FiAlertTriangle size={14} />
                    <span>{e.message}</span>
                </div>
            ))}

            <div className="flex items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                >
                    Cancelar
                </button>
                <button
                    type="button"
                    onClick={form.submit}
                    disabled={isSaving || !form.canSubmit}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                    {isSaving ? 'Salvando...' : 'Registrar depósito'}
                </button>
            </div>
        </div>
    );
};

export default LegalCaseDepositForm;
